/**
 * LocationPicker - A reusable component for selecting or creating locations
 */

import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  LocationSummary,
  LocationCreate,
  listLocations,
  createLocation,
} from "../../api/locations";

interface LocationPickerProps {
  value: number | null;
  onChange: (locationId: number | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
  className?: string;
}

export default function LocationPicker({
  value,
  onChange,
  placeholder = "Select a location...",
  allowCreate = true,
  className = "",
}: LocationPickerProps) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLocation, setNewLocation] = useState<LocationCreate>({ name: "" });

  // Fetch locations
  const { data: locations, isLoading } = useQuery({
    queryKey: ["locations-picker", search],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listLocations(token, { search: search || undefined, limit: 50 });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: LocationCreate) => {
      const token = await getAccessTokenSilently();
      return createLocation(token, data);
    },
    onSuccess: (newLoc) => {
      queryClient.invalidateQueries({ queryKey: ["locations-picker"] });
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      onChange(newLoc.id);
      setShowCreateForm(false);
      setNewLocation({ name: "" });
      setIsOpen(false);
    },
  });

  // Find selected location name
  const selectedLocation = locations?.find((loc) => loc.id === value);

  const handleSelect = (loc: LocationSummary) => {
    onChange(loc.id);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = () => {
    onChange(null);
  };

  const handleCreateSubmit = () => {
    if (newLocation.name.trim()) {
      createMutation.mutate(newLocation);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected value display / trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full rounded border border-vault-200 px-3 py-2 bg-white cursor-pointer hover:border-vault-300"
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedLocation ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate text-vault-900">{selectedLocation.name}</span>
              {selectedLocation.city && (
                <span className="text-vault-500 text-sm truncate">({selectedLocation.city})</span>
              )}
            </>
          ) : (
            <span className="text-vault-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 text-vault-400 hover:text-vault-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-vault-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-vault-200 bg-white shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-vault-100">
            <input
              type="text"
              placeholder="Search locations..."
              className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          {/* Location list */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 text-center text-vault-500 text-sm">Loading...</div>
            ) : locations && locations.length > 0 ? (
              locations.map((loc) => (
                <div
                  key={loc.id}
                  onClick={() => handleSelect(loc)}
                  className={`px-3 py-2 cursor-pointer hover:bg-vault-50 flex items-center gap-2 ${
                    loc.id === value ? "bg-vault-100" : ""
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="min-w-0">
                    <div className="text-sm text-vault-900 truncate">{loc.name}</div>
                    {(loc.city || loc.country) && (
                      <div className="text-xs text-vault-500 truncate">
                        {[loc.city, loc.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-vault-500 text-sm">
                {search ? "No locations found" : "No locations yet"}
              </div>
            )}
          </div>

          {/* Create new option */}
          {allowCreate && (
            <div className="border-t border-vault-100">
              {showCreateForm ? (
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    placeholder="Location name"
                    className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="City (optional)"
                      className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
                      value={newLocation.city || ""}
                      onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Country (optional)"
                      className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
                      value={newLocation.country || ""}
                      onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 rounded bg-vault-100 px-3 py-1.5 text-sm text-vault-700 hover:bg-vault-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateSubmit}
                      disabled={!newLocation.name.trim() || createMutation.isPending}
                      className="flex-1 rounded bg-vault-600 px-3 py-1.5 text-sm text-white hover:bg-vault-700 disabled:opacity-50"
                    >
                      {createMutation.isPending ? "Creating..." : "Create"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full px-3 py-2 text-left text-sm text-vault-600 hover:bg-vault-50 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create new location
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setShowCreateForm(false);
          }}
        />
      )}
    </div>
  );
}
